import React, { useRef, useState } from "react"
import {
  Box, FormControl, FormHelperText, FormLabel, Image, Text,
} from "@chakra-ui/react"
import { Control, FieldValues, useController } from "react-hook-form"
import ImageUploadComponent from "components/ImageUploadComponent"

type Props<T extends FieldValues> = {
  name: string;
  control: Control<T, object>;
  alt?: string;
  label?: string;
  description?: React.ReactNode;
  vertical?: boolean;
  imageUrl?: string;
  imageWidth?: string|number;
  imageHeight?: string|number;
  imageMaxWidth?: string|number;
  imageMaxHeight?: string|number;
  imageFit?: "contain" | "cover";
  hideImageBorder?: boolean;
  accept?: string;
  Component?: React.FC<any>;
  helperText?: string;
  disabled?: boolean;
  labelSize?: "sm" | "md" | "lg";
  labelWeight?: "normal" | "medium" | "semibold" | "bold";
  maxW?: string | number;
}

const ImageUpload = <T extends FieldValues, >({
  name,
  control,
  alt,
  label,
  description,
  vertical,
  imageUrl,
  imageWidth = "auto",
  imageHeight = "auto",
  imageMaxWidth,
  imageMaxHeight,
  imageFit,
  hideImageBorder,
  accept = "image/*",
  helperText,
  disabled,
  Component = ImageUploadComponent,
  labelSize,
  labelWeight,
  maxW,
}: Props<T>) => {
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [removed, setRemoved] = useState(false)
  const [touched, setTouched] = useState(false)
  const { field } = useController<any>({ name, control })
  const { field: urlField } = useController<any>({ name: `${name}Url`, control })

  const fileInputProps = {
    ref: fileInputRef,
    id: name,
    type: "file",
    accept,
    onChange: (e) => {
      setRemoved(false)
      field.onChange(e.target.files[0])
      setTouched(true)
      e.target.value = null
    },
  }

  let imgSrc = imageUrl
  if (touched && field.value) {
    try {
      imgSrc = URL.createObjectURL(field.value)
    } catch (e) {
      imgSrc = imageUrl
    }
  }

  let imagePreview
  if (imgSrc) {
    imagePreview = (
      <Image
        ignoreFallback
        src={imgSrc}
        alt={alt}
        w={imageWidth}
        h={imageHeight}
        maxW={imageMaxWidth}
        maxH={imageMaxHeight}
        fit={imageFit}
        borderWidth={hideImageBorder ? 0 : 1}
        borderRadius="md"
        borderColor="gray.400"
        borderStyle="solid"
      />
    )
  }

  const controlContent = (
    <>
      <Component
        imagePreview={imagePreview}
        fileInputRef={fileInputRef}
        removed={removed}
        onRemove={() => {
          field.onChange(null)
          urlField.onChange(null)
          setRemoved(true)
        }}
        disabled={disabled}
      />
      {helperText && <FormHelperText>{helperText}</FormHelperText>}
      <input {...fileInputProps} style={{ display: "none" }} data-testid="image-upload" />
    </>
  )

  return (
    <FormControl mb={4} display={vertical ? "initial" : "flex"} alignItems="center">
      {label && (
        <FormLabel
          w={vertical ? "auto" : 1 / 4}
          fontSize={labelSize}
          fontWeight={labelWeight}
        >
          {label}
        </FormLabel>
      )}
      <Box w="100%">
        {description && (
          <Text fontSize="md" mb={3}>
            {description}
          </Text>
        )}
        {maxW != null ? <Box maxW={maxW}>{controlContent}</Box> : controlContent}
      </Box>
    </FormControl>
  )
}

export default ImageUpload
